import React, { useEffect, useRef, useCallback } from "react";
import { COS_URL } from "../../const/constants.js";
import Header from "./Header/index.jsx";
import GeneralBasicOCR from "../features/GeneralBasicOCR.jsx";
import OcrUpload from "../ui/Upload.jsx";

import ThumbnailPreview from "../ui/ThumbnailPreview.jsx";
import ImageOptimize from "../features/ImageOptimize";
import { Thumbnail } from "../ui/universe";
import { getRectPosition, customRound } from "../../utils/index";
import useSmoothScroll from "react-smooth-scroll-hook";
import Sider from "./Sider/index.jsx";
import { useMenuStore, useImageStore, useUIStore } from "../../store";
import "../../style/layout.css";

const imgFileType = ["jpg", "jpeg", "png", "bmp", "JPG", "JPEG", "PNG", "BMP"];

function OcrLayout({ onChangeAction }) {
  // 从 menuStore 获取状态（使用选择器确保正确订阅）
  const currentMenu = useMenuStore((state) => state.currentMenu);

  // 从 imageStore 获取状态（使用选择器确保正确订阅）
  const imgList = useImageStore((state) => state.imgList);
  const uploadImgList = useImageStore((state) => state.uploadImgList);
  const imgListType = useImageStore((state) => state.imgListType);
  const currentImage = useImageStore((state) => state.currentImage);
  const range = useImageStore((state) => state.range);
  const imgPreType = useImageStore((state) => state.imgPreType);
  const rectDocuments = useImageStore((state) => state.rectDocuments);
  // 从 imageStore 获取操作函数
  const {
    setImgList,
    setImgListType,
    setCurrentImage,
    setAngle,
    setRange,
    setImgPreType,
    setShowImgList,
    setRects,
    setRectDocuments,
    setSelectRectKey,
    setClipImg,
    setTotalPage,
    setPrefix,
    setIsImageError,
    clearImageState,
    addUploadImage,
    removeUploadImage,
    clearUploadImgList,
  } = useImageStore();

  // 从 uiStore 获取状态（使用选择器确保正确订阅）
  const isLoading = useUIStore((state) => state.isLoading);
  const setIsLoading = useUIStore((state) => state.setIsLoading);

  // 本地状态 - 仅保留需要 ref 或临时状态
  const [result, setResult] = React.useState(null);
  const [showThumListBtn, setShowThumListBtn] = React.useState(false);

  const uploadRef = useRef();
  const imgPreRef = useRef();
  const GeneralBasicOCRRef = useRef();
  const domRef = useRef(document.body);
  const thumbnailRef = useRef();

  const { scrollTo } = useSmoothScroll({
    ref: domRef,
    speed: 100,
    direction: "x",
  });

  // 稳定回调引用，避免 ThumbnailPreview useEffect 依赖变化导致无限循环
  const handlePreviewSelect = useCallback(
    (val) => {
      setSelectRectKey(val);
    },
    [setSelectRectKey],
  );

  const handlePreviewClip = useCallback(
    (val) => {
      setClipImg(val);
    },
    [setClipImg],
  );

  // 切换图片/项目时，清空rect
  useEffect(() => {
    if (!currentMenu) return;
    clearImageState();
  }, [currentMenu, currentImage?.url]);

  // 切换项目时，清空上传列表
  useEffect(() => {
    clearUploadImgList();
    setImgListType("default");
  }, [currentMenu]);

  // rectDocuments 变化时更新 rects
  useEffect(() => {
    if (rectDocuments.length > 0) {
      const _rects = rectDocuments.map((r) => ({
        ...r,
        visible: true,
      }));
      setRects(_rects);
    } else {
      setRects([]);
    }
  }, [rectDocuments]);

  // currentMenu 变化时设置配置
  useEffect(() => {
    if (currentMenu) {
      setConfig(currentMenu);
    }
  }, [currentMenu]);

  const setConfig = (_result) => {
    if (!_result) return;
    const _prefix = _result.prefix || "Image";
    setPrefix(_prefix);
    const _imgList = _result?.imglist?.map((i, index) => {
      if (typeof i === "string") {
        return {
          previewUrl: i.indexOf("https") > -1 ? i : COS_URL + i,
          url: i.indexOf("https") > -1 ? i : COS_URL + i,
          OriginUrl: i.indexOf("https") > -1 ? i : COS_URL + i,
          cantPreview: _result?.fakeImglist?.length > 0,
          type: "url",
          isExample: true,
          pdfImgList: [],
          fileType: i?.split("?")[0].split(".").pop().toLowerCase(),
          prefix: _result?.ImageParams ? _result?.ImageParams : `${_prefix}Url`,
          param: _result?.fakeImglist
            ? i.indexOf("https") > -1
              ? _result?.fakeImglist[index]
              : COS_URL + _result?.fakeImglist[index]
            : "",
        };
      } else {
        return {
          previewUrl: i.url.indexOf("https") > -1 ? i.url : COS_URL + i.url,
          url: i.url.indexOf("https") > -1 ? i.url : COS_URL + i.url,
          defaultConfig: i.defaultConfig,
          OriginUrl: i.url.indexOf("https") > -1 ? i.url : COS_URL + i.url,
          cantPreview: _result?.fakeImglist?.length > 0,
          type: "url",
          isExample: true,
          pdfImgList: [],
          fileType: i.url.split("?")[0].split(".").pop().toLowerCase(),
          prefix: _result?.ImageParams ? _result?.ImageParams : `${_prefix}Url`,
          param: _result?.fakeImglist
            ? i.indexOf("https") > -1
              ? _result?.fakeImglist[index]
              : COS_URL + _result?.fakeImglist[index]
            : "",
        };
      }
    });
    setImgList(_imgList);
    setCurrentImage(_imgList?.[0]);
    setTotalPage(1);
    setResult(_imgList?.[0]);
    setImgPreType(_result?.isImageResult ? "split" : "preview");

    // 处理图片显示
    if (domRef?.current?.clientWidth < _imgList?.length * 94) {
      setShowThumListBtn(true);
    } else {
      setShowThumListBtn(false);
    }

    // 首次加载时触发识别
    if (_imgList?.[0]) {
      setTimeout(() => {
        GeneralBasicOCRRef?.current?.start({
          image: _imgList[0],
          config: _imgList[0].defaultConfig,
        });
      }, 0);
    }
  };

  const handleImageClick = (img, isUpload = false) => {
    if (isLoading) return;
    setCurrentImage(img);
    setTotalPage(img.pdfImgList?.length || 1);
    setResult(img);
    GeneralBasicOCRRef?.current?.start({
      image: img,
      config: isUpload ? undefined : img.defaultConfig,
    });
  };

  const handleDeleteUploadImage = (e, index) => {
    e.stopPropagation();
    removeUploadImage(index);
  };

  return (
    <div className="pg-recognize">
      <Header onChangeAction={onChangeAction} />
      <div className="pg-layout">
        <Sider />
        <div
          className="auto-flex"
          style={{ background: "#F4F5F7", height: "100%", flex: 1 }}
        >
          <div className="preview_container">
            <>
              {imgPreType === "preview" ? (
                <ThumbnailPreview
                  cRef={imgPreRef}
                  style={{ background: "#fff" }}
                  onUpload={() => {
                    uploadRef.current.onUpload();
                  }}
                  onSelect={handlePreviewSelect}
                  onClip={handlePreviewClip}
                  imageList={
                    currentImage?.pdfImgList?.length > 0
                      ? currentImage.pdfImgList
                      : []
                  }
                  imageUrl={currentImage?.previewUrl || currentImage?.url}
                />
              ) : (
                <>
                  <ImageOptimize
                    type="split"
                    defaultRange={range}
                    getRange={(e) => setRange(e)}
                    imgList={[
                      `data:image/png;base64,${result?.url}`,
                      currentImage?.pdfImgList?.[0]?.ImageUrl ??
                        currentImage?.url,
                    ]}
                    tips={currentMenu?.imageResultTips || ["增强后", "原图"]}
                  />
                </>
              )}
            </>
          </div>
          <div className="ocrDemo-imgListType-container">
            <div
              className={`ocrDemo-imgListType-item ${imgListType === "default" ? "active" : ""}`}
              onClick={() => setImgListType("default")}
            >
              示例文件
            </div>
            <div className="ocrDemo-imgListType-slice"></div>
            <div
              className={`ocrDemo-imgListType-item ${imgListType === "upload" ? "active" : ""}`}
              onClick={() => setImgListType("upload")}
            >
              上传文件
            </div>
            {imgListType === "upload" && uploadImgList.length > 0 && (
              <div
                className={`ocrDemo-imgListType-del ${uploadImgList?.length > 0 ? "" : "disable"}`}
                onClick={() => clearUploadImgList()}
              >
                <span className="ocrDemo-icon ocrDemo-icon__del"></span>
                <span>清除所有上传</span>
              </div>
            )}
          </div>
          <div className="imgList_scrollView-container">
            <div className="imgList_scrollView" ref={domRef}>
              <div className="unv-thumbnail-list" ref={thumbnailRef}>
                {imgListType === "upload" && (
                  <>
                    <div
                      className="ocrDemo-icon_uploadEmpty"
                      onClick={() => {
                        uploadRef.current.onUpload();
                      }}
                    ></div>
                    {uploadImgList?.map((i, index) => (
                      <div
                        key={i.url || index}
                        className="ocrDemo-imgItem-container uploadList"
                        onClick={() => handleImageClick(i, true)}
                      >
                        <Thumbnail
                          fileType={i.fileType}
                          active={
                            currentImage?.url === i.url ||
                            currentImage?.OriginUrl === i.OriginUrl
                          }
                          imageUrl={i.previewUrl || i.url}
                        />
                        <div className="ocrDemo-imgItem__delete">
                          <div
                            className="ocrDemo-icon_delete"
                            onClick={(e) => handleDeleteUploadImage(e, index)}
                          ></div>
                        </div>
                        {i.fileType && (
                          <div
                            className={`ocrDemo-imgItem__fileType ${imgFileType.includes(i.fileType) ? "img" : "file"}`}
                          >
                            {i.fileType}
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
                {imgListType === "default" &&
                  imgList?.map((i, index) => (
                    <div
                      key={i.url || index}
                      className="ocrDemo-imgItem-container"
                      onClick={() => handleImageClick(i)}
                    >
                      <Thumbnail
                        fileType={i.fileType}
                        active={
                          currentImage?.url === i.url ||
                          currentImage?.OriginUrl === i.OriginUrl
                        }
                        imageUrl={i.previewUrl || i.url}
                      />
                      {i.fileType && (
                        <div
                          className={`ocrDemo-imgItem__fileType ${imgFileType.includes(i.fileType) ? "img" : "file"}`}
                        >
                          {i.fileType}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
            <div
              className={`${showThumListBtn ? "isActive" : "isHide"} imgList_scrollView__btn`}
              onClick={() => scrollTo(200)}
            ></div>
          </div>
          <OcrUpload
            cRef={uploadRef}
            onSuccess={({ img }) => {
              setImgListType("upload");
              setIsImageError(true);
              GeneralBasicOCRRef?.current?.start({ image: img });
            }}
          />
        </div>
        <div
          style={{
            background: "#F4F5F7",
            width: "25%",
            maxWidth: "25%",
            minWidth: "320px",
            padding: "20px",
            flex: 1,
          }}
        >
          <GeneralBasicOCR
            onSelect={(_, rectVal) => {
              setSelectRectKey(rectVal);
              imgPreRef?.current?.onRectChange(rectVal);
            }}
            cRef={GeneralBasicOCRRef}
            onResult={({
              isError = false,
              image,
              rsp,
              requestParams,
              config = currentMenu,
              rect,
            }) => {
              setIsLoading(true);
              const _config = config || currentMenu;
              const pdfList =
                rsp.auditData?.length > 0 && rsp.auditData[0].ImageUrl
                  ? rsp.auditData
                  : [];

              // 处理角度设置
              if (
                [0, 90, 180, 270, 360].includes(customRound(rsp?.Angle)) &&
                pdfList.length <= 0 &&
                currentMenu?.supportAngle
              ) {
                setAngle(customRound(rsp.Angle));
              } else {
                setAngle(0);
              }

              setTotalPage(rsp?.auditData?.length || 1);

              // 处理QuestionOCR相关逻辑
              const handleQuestionOCR = () => {
                let previewUrl =
                  pdfList.length > 0 ? pdfList[0].ImageUrl : image.url;
                let imgSize = null;

                if (rsp && rsp?.QuestionInfo?.length > 0) {
                  const qAngle = customRound(rsp?.QuestionInfo[0]?.Angle || 0);
                  const isRotated90or270 = qAngle === 90 || qAngle === 270;
                  const hasBase64 = !!rsp?.QuestionInfo[0]?.ImageBase64;

                  if (hasBase64) {
                    // Crop correction enabled: server returns a rotated/corrected base64 image.
                    // Swap width/height when angle is 90/270 to match the corrected image dimensions.
                    imgSize = {
                      width: isRotated90or270
                        ? rsp?.QuestionInfo[0].Height
                        : rsp?.QuestionInfo[0].Width,
                      height: isRotated90or270
                        ? rsp?.QuestionInfo[0].Width
                        : rsp?.QuestionInfo[0].Height,
                    };
                    previewUrl =
                      `data:image/png;base64,` +
                      rsp.QuestionInfo[0].ImageBase64;
                  } else {
                    // Crop correction disabled: no base64 returned.
                    // Frontend strips EXIF so the image renders in its original orientation.
                    // Use the API's original width/height directly — no swap needed.
                    imgSize = {
                      width: rsp?.QuestionInfo[0].Width,
                      height: rsp?.QuestionInfo[0].Height,
                    };
                  }
                }

                const _curImg = {
                  ...image,
                  cantPreview: false,
                  pdfImgList: [],
                  OriginUrl: image.url,
                  url: image.url,
                  previewUrl: previewUrl,
                };

                updateCurrentImage(_curImg, !_curImg.isExample);
                imgPreRef?.current?.onPreChange({
                  curImg: previewUrl,
                  curpPdfList: [],
                  imgSize: imgSize,
                  stripExif: true,
                });
              };

              // 处理其他OCR逻辑
              const handleOtherOCR = () => {
                let imgSize = null;
                if (rsp && rsp?.QuestionInfo?.length > 0) {
                  // Frontend strips EXIF so the image renders in its original orientation.
                  // Use the API's original width/height directly — no swap needed.
                  imgSize = {
                    width: rsp?.QuestionInfo[0].Width,
                    height: rsp?.QuestionInfo[0].Height,
                  };
                }
                imgPreRef?.current?.onPreChange({
                  curImg: image.url,
                  curpPdfList: pdfList,
                  imgSize: imgSize,
                });

                const _curImg = {
                  ...image,
                  pdfImgList: pdfList,
                  previewUrl:
                    pdfList.length > 0
                      ? pdfList[0].ImageUrl
                      : image.previewUrl || image.url,
                  cantPreview: false,
                };

                updateCurrentImage(_curImg, !_curImg.isExample);
              };

              // 更新当前图片和上传列表
              const updateCurrentImage = (_curImg, shouldUpdateUploadList) => {
                setCurrentImage(_curImg);
                if (shouldUpdateUploadList) {
                  addUploadImage(_curImg);
                }
              };

              // 处理矩形框数据
              const handleRectData = () => {
                if (rect?.length > 0) {
                  return rect;
                }

                let _rects = [];
                const isWordListOnly = _config?.rectConfig?.showWordListOnly;
                const showWordList =
                  _config?.rectConfig?.useWordList && rsp?.WordList?.length > 0;

                if (isWordListOnly) {
                  _rects = processRectConfig(
                    _config,
                    rsp,
                    pdfList,
                    requestParams,
                    "WordListRectConfig",
                  );
                } else {
                  if (showWordList) {
                    _rects = processRectConfig(
                      _config,
                      rsp,
                      pdfList,
                      requestParams,
                      "WordListRectConfig",
                    );
                  }
                  if (showWordList || !isWordListOnly) {
                    const additionalRects = processRectConfig(
                      _config,
                      rsp,
                      pdfList,
                      requestParams,
                      "rectConfig",
                    );
                    _rects.push(...additionalRects);
                  }
                }

                return _rects;
              };

              // 处理矩形配置
              const processRectConfig = (
                config,
                data,
                pdfListData,
                reqParams,
                configType,
              ) => {
                const resultRects = [];
                const configData = config[configType];

                if (Array.isArray(configData)) {
                  configData.forEach((configItem) => {
                    const rectResult = getRectPosition({
                      data: data,
                      config: configItem,
                      isMulPage: pdfListData.length > 1,
                      total: pdfListData.length,
                      PdfPageNumber: reqParams?.PdfPageNumber || 1,
                    });
                    resultRects.push(...rectResult);
                  });
                } else if (configData) {
                  const rectResult = getRectPosition({
                    data: data,
                    config: configData,
                    isMulPage: pdfListData.length > 1,
                    total: pdfListData.length,
                    PdfPageNumber: reqParams?.PdfPageNumber || 1,
                  });
                  resultRects.push(...rectResult);
                }
                return resultRects;
              };

              // 主逻辑分发
              if (
                [
                  "QuestionOCR",
                  "QuestionSplitOCR",
                  "QuestionSplitLayoutOCR",
                  "SubmitQuestionMarkAgentJob",
                ].includes(_config.action) ||
                _config.actionType === "markEdu"
              ) {
                handleQuestionOCR();
              } else {
                handleOtherOCR();
              }

              if (!isError) {
                setTimeout(() => {
                  const _rects = handleRectData();
                  setRectDocuments(_rects);
                  setShowImgList(false);
                }, 0);
              }

              setIsLoading(false);
            }}
            onImageError={(res) => {
              setIsImageError(res);
              if (res) {
                setCurrentImage("");
              }
            }}
            onChangeImg={(img, params) => {
              const _curImg = {
                ...currentImage,
                url: img,
                previewUrl: img,
                OriginUrl: img,
              };
              setCurrentImage(_curImg);
              GeneralBasicOCRRef?.current?.start({
                image: _curImg,
                config: params,
              });
            }}
            onChangeImageEnhancement={(res) => {
              setResult({
                url: res.url,
              });
              setImgPreType(res.type);
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default OcrLayout;
